/**
 * SpiceWeekTab: the whole-team SPICE grid, therapists down, Monday to
 * Friday across (Saturday and Sunday too, in a week a session falls on
 * them), each cell its visits in time order, each visit one pill.
 *
 * Rendered by the Team page for every seat admitted to the page (the
 * Directory tab's floor, view_staff_tools); unlike Capacity and Map,
 * this tab stays visible for a SPICE therapist without view_workspace.
 * Every seat reads the grid. A viewer who may change a client's schedule
 * (canEditClientSchedule) gets the grid as a working editor of the team's
 * week, not just a second door onto one client's row:
 *   - every pill's time (a paused pill's away phrase) opens the SAME
 *     schedule editor the client record uses (SpiceScheduleEditDialog), so the
 *     grid is a live editor of the one spice_visit_plans row per client,
 *     never a separate copy of it. From here the editor also carries the
 *     plan-level SPICE therapist select, opened on the client's own
 *     therapist whichever row the pill sits on, so the client's own
 *     therapist can be changed without leaving the page; each session's
 *     own Therapist is on its card;
 *   - every cell carries an Add door: pick a SPICE client from the
 *     all-clients SPICE view and the editor opens on THAT weekday, so a
 *     visit is set in two clicks. A client with a therapist and a
 *     schedule keeps her therapist and the session added takes the row's;
 *     a client with no therapist yet, or no schedule yet, gets the row's
 *     therapist as her own;
 *   - a therapist with nothing this week still gets a row (with Add
 *     doors) for an editor; for a viewer those names collapse into one
 *     footer line;
 *   - SPICE clients who are not on the grid at all (no schedule yet, or
 *     no therapist yet) are listed under it, each a door onto the editor;
 *   - every pill that is the client's own carries a remove × inside it,
 *     overlaid on its right end and shown on hover or focus: a pill of a
 *     plan with several sessions saves the plan without that session; a
 *     plan's last session removes the schedule whole and the client moves
 *     to "Not scheduled yet"; either leaves an Undo toast that re-saves
 *     the plan as it was. A cover pill gets no ×: the schedule is the
 *     owning therapist's row's to remove (the editor still opens on it);
 *   - every pill that is the client's own can also be dragged: to another
 *     day on its row (the plan PUT with that one session's weekday
 *     changed, its time kept, a monthly session taking the week of the
 *     month of the day it lands on in the week in view) or onto another
 *     therapist's row (the plan PUT with that one session's therapist and
 *     day changed; the client's other sessions, her own therapist and the
 *     plan's cover stay as they are, and no drag writes the engagement),
 *     by pointer from anywhere on the pill, or by keyboard from the pill
 *     itself (space lifts, the arrow keys step between Monday and Friday
 *     cells, space drops, escape puts it back), each step announced for
 *     a screen reader. A weekend day is set in the editor: the keyboard
 *     stops at Friday and a drag cannot reach a column the week does not
 *     show. What a drop writes, and what it refuses, is decided in
 *     src/lib/spiceScheduleMove.ts; a move leaves an Undo toast that
 *     puts the plan back exactly, in one PUT. A cover pill is never
 *     lifted.
 * A line clinician's pills carry no door but the name. For every reader,
 * the client's name is a link to that client's SPICE tab on the client
 * record.
 *
 * Today's column is marked. A pill reads the schedule only: the time and
 * the client; note status belongs on the notes surfaces, so the outcome
 * the wire carries is not shown here. The kind reads by the pill's
 * colour (the owner's two colours, useSpiceKindColors), the legend above
 * the grid, and the pill's accessible name. A pill is filled while its
 * visit is this therapist's to make, outlined with one quiet "Covering:
 * {name}" line under it on the regular therapist's row while another
 * therapist covers, and greyed with the away phrase in place of the time
 * while the client is paused.
 *
 * Each day column has a 7rem floor: the grid scrolls inside its own
 * wrapper below that, and the page never scrolls sideways.
 */

import { useCallback, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Active,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
  type Over,
} from '@dnd-kit/core';
import { useTenant } from '@/lib/auth';
import { canEditClientSchedule } from '@/lib/capabilities';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { SearchField } from '@/components/primitives/SearchField';
import { FLD_LABEL } from '@/components/primitives/modalFormStyles';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import { addDays, calendarDateIn, currentWeekMonday, isoWeekMonday, weekLabel } from '@/lib/capacity';
import {
  SPICE_KIND_LABELS,
  SPICE_WEEK_OF_MONTH_LABELS,
  formatSpiceAway,
  formatSpiceScheduleText,
  formatSpiceShortDate,
  formatSpiceTime,
} from '@/lib/spiceScheduleCopy';
import { useSpiceClientsList, type SpiceClientRosterRowWire } from '@/lib/clientsApi';
import { contactSentence, errorCodeOf, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  nextSpiceGridCell,
  planSpiceMove,
  spiceCellDropId,
  spiceEntryDragId,
  spiceMoveRefusalLine,
  type SpiceGridCell,
} from '@/lib/spiceScheduleMove';
import { useUndo } from '@/lib/undo';
import {
  fetchClientSpicePlan,
  spicePlanToInput,
  spicePlanToVitals,
  spicePlanWithoutSession,
  useClientSpicePlan,
  useDeleteClientSpicePlan,
  useSetClientSpicePlan,
  useSpiceWeek,
  withSessionTherapistKeys,
  type SpiceScheduleVitals,
  type SpiceWeekGridEntryWire,
  type SpiceWeekGridTherapistWire,
} from '@/lib/spiceVisitPlansApi';
import { SpiceScheduleEditDialog, type SpiceScheduleTherapistContext } from '@/components/clientRecord/VitalsBand';
import { SpiceKindLegend } from '@/components/clientRecord/SpiceKindMark';
import { useSpiceKindColors } from '@/hooks/useSpiceKindColors';
import type { SpiceKindColor } from '@/lib/spiceKindColor';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const WEEKDAYS: { weekday: number; label: string; long: string }[] = [
  { weekday: 1, label: 'Mon', long: 'Monday' },
  { weekday: 2, label: 'Tue', long: 'Tuesday' },
  { weekday: 3, label: 'Wed', long: 'Wednesday' },
  { weekday: 4, label: 'Thu', long: 'Thursday' },
  { weekday: 5, label: 'Fri', long: 'Friday' },
  { weekday: 6, label: 'Sat', long: 'Saturday' },
  { weekday: 7, label: 'Sun', long: 'Sunday' },
];

/** The week's columns: Monday to Friday always, Saturday and Sunday only
 *  when an entry that week falls on them (a paused one included; the server
 *  already leaves out an every-two-weeks session's off week). */
function spiceWeekColumns(therapists: readonly SpiceWeekGridTherapistWire[]): typeof WEEKDAYS {
  const used = new Set(therapists.flatMap((t) => t.entries.map((e) => e.weekday)));
  return WEEKDAYS.filter((d) => d.weekday <= 5 || used.has(d.weekday));
}

function weekdayLong(weekday: number): string {
  return WEEKDAYS.find((d) => d.weekday === weekday)?.long ?? '';
}

/** The line the schedule route sends with 409 reload_required: a page
 *  loaded before the shape the server now expects (here, a session's
 *  therapist) sends a body it will not translate. */
const RELOAD_REQUIRED_LINE = "That didn't save because this page is out of date. Reload the page, then save again.";

/** The grid's line for a move that failed for no reason the grid can name. */
function moveFailedLine(clientName: string, contact: SupportContact): string {
  return contactSentence(
    `${clientName}'s visit didn't move. Try again, and if it happens again, tell {contact_system}.`,
    contact,
  );
}

/** The grid's line for a drop the server refused, chosen by its code:
 *  the row's therapist cannot take SPICE visits (422
 *  not_a_spice_therapist), the page must reload (409 reload_required),
 *  or the house line for anything else. */
function moveRefusedLine(
  err: unknown,
  clientName: string,
  therapistName: string,
  contact: SupportContact,
): string {
  const code = errorCodeOf(err);
  if (code === 'not_a_spice_therapist') {
    return `${therapistName} isn't a SPICE therapist, so ${clientName}'s visit didn't move. Drop it on a SPICE therapist's row.`;
  }
  if (code === 'reload_required') return RELOAD_REQUIRED_LINE;
  return moveFailedLine(clientName, contact);
}

/** What the grid is opening the editor for. */
interface EditingTarget {
  clientId: string;
  clientName: string;
  /** The therapist row the plan-level SPICE therapist select opens on (an
   *  Add door for a client who has no therapist yet); undefined when the
   *  select opens on the client's own therapist (an entry, an Add door
   *  for a client who keeps her therapist, or the "not on the grid"
   *  list). */
  therapistRosterId?: string | null;
  /** The therapist the first session added takes (an Add door on another
   *  therapist's row, for a client who keeps her own therapist). */
  sessionTherapistRosterId?: string;
  /** The weekday column the door was opened from (an Add door). */
  weekday?: number;
  /** The date of the cell or pill the door was opened from: a session
   *  switched to Monthly takes its week of the month from it. */
  date?: string;
}

/** An Add door's context: which row and column it sits in. */
interface AddTarget {
  therapistRosterId: string;
  therapistName: string;
  weekday: number;
  /** The cell's date in the week in view. */
  date: string;
}

/**
 * The small loader that lets a grid door open the SAME dialog the client
 * record's Vitals band uses, from a page that has no client-record
 * aggregate to read the current plan off of: the My week precedent
 * (src/components/home/MyWeekEditScheduleDialog.tsx), scoped to the Team
 * page's grid, plus the grid-only extras (the client's name in the title,
 * the Therapist select, the preset weekday).
 */
function GridScheduleEditDialog({
  tenantSlug,
  target,
  therapistContext,
  onClose,
}: {
  tenantSlug: string;
  target: EditingTarget;
  /** The client's current therapist and engagement, from the SPICE view;
   *  undefined while that list has not loaded (the select is then
   *  omitted rather than shown with a wrong "current"). */
  therapistContext: SpiceScheduleTherapistContext | undefined;
  onClose: () => void;
}): ReactNode {
  const { data, isLoading, isError } = useClientSpicePlan(tenantSlug, target.clientId);

  const plan: SpiceScheduleVitals | null = data?.plan ? spicePlanToVitals(data.plan) : null;

  // A loading/error beat renders as a minimal inline note rather than the
  // full dialog chrome popping in twice (once empty, once filled), the My
  // week precedent this mirrors.
  if (isLoading) {
    return (
      <div role="status" data-testid="spice-week-edit-schedule-loading" className="sr-only">
        Loading schedule…
      </div>
    );
  }
  const shared = {
    onClose,
    clientId: target.clientId,
    tenantSlug,
    clientName: target.clientName,
    therapist: therapistContext,
    spiceTherapistRosterId: data?.therapist_roster_id ?? null,
    initialSessionTherapistRosterId: target.sessionTherapistRosterId ?? null,
    initialWeekday: target.weekday,
    initialDate: target.date,
  };
  // On a failed prefill read, open on sensible defaults rather than block
  // the edit door; the save path re-validates server-side.
  return <SpiceScheduleEditDialog plan={isError ? null : plan} {...shared} />;
}

/**
 * The Add door's picker: every SPICE client (the all-clients SPICE view),
 * filtered by name as the editor types, each row saying who has them now
 * and what their schedule is, so moving a client onto this row is a
 * deliberate pick, not a guess.
 */
function AddVisitPicker({
  target,
  clients,
  isLoading,
  isError,
  onPick,
  onClose,
}: {
  target: AddTarget;
  clients: readonly SpiceClientRosterRowWire[];
  isLoading: boolean;
  isError: boolean;
  onPick: (client: SpiceClientRosterRowWire) => void;
  onClose: () => void;
}): ReactNode {
  const [query, setQuery] = useState('');
  const weekday = weekdayLong(target.weekday);
  const rows = filterSpiceClients(clients, query);
  return (
    <Dialog
      title={`Add a visit: ${target.therapistName}, ${weekday}`}
      size="md"
      testId="spice-week-add-picker"
      onClose={onClose}
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <div className="py-2">
        <label htmlFor="spice-week-add-search" className={FLD_LABEL}>
          Which client?
        </label>
        <SearchField
          id="spice-week-add-search"
          value={query}
          onChange={setQuery}
          placeholder="Search by name"
          testId="spice-week-add-search"
        />
        <p className="mt-1.5 text-xs text-ink-soft">
          A care-management-only client becomes a SPICE client on their record first.
        </p>
        <ul className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-sage-400/25 py-1" data-testid="spice-week-add-results">
          {isLoading && (
            <li className="px-3 py-2 text-xs text-ink-soft" data-testid="spice-week-add-loading">
              Loading SPICE clients.
            </li>
          )}
          {isError && (
            <li role="alert" className="px-3 py-2 text-xs text-danger">
              The SPICE client list didn't load. Close this and open it again.
            </li>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <li className="px-3 py-2 text-xs text-ink-soft" data-testid="spice-week-add-empty">
              {clients.length === 0 ? "Set a client to SPICE on their record and you'll see them here." : 'Try another spelling.'}
            </li>
          )}
          {rows.map((c) => {
            const here = c.therapist_roster_id === target.therapistRosterId;
            const who = c.therapist_name ? (here ? 'this therapist' : `with ${c.therapist_name}`) : 'no therapist yet';
            return (
              <li key={c.id}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full !justify-start !whitespace-normal text-left"
                  onClick={() => onPick(c)}
                  data-testid={`spice-week-add-option-${c.id}`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span>{c.display_name}</span>
                    <span className="font-normal text-xs text-ink-soft">
                      {who} · {scheduleWords(c)}
                    </span>
                  </span>
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
}

/** The picker's rows for a query: every SPICE client whose name contains
 *  it (all of them for an empty query), in the list's own order. */
export function filterSpiceClients(
  clients: readonly SpiceClientRosterRowWire[],
  query: string,
): SpiceClientRosterRowWire[] {
  const q = query.trim().toLowerCase();
  return clients.filter((c) => q === '' || c.display_name.toLowerCase().includes(q));
}

/** The client's schedule in words, off the SPICE view's row. */
function scheduleWords(c: SpiceClientRosterRowWire): string {
  return formatSpiceScheduleText(c.schedule?.sessions ?? []);
}

/** SPICE clients the grid cannot show: no schedule yet, or no therapist
 *  to sit them under. Exported for a direct unit test. */
export function spiceClientsOffTheGrid(clients: readonly SpiceClientRosterRowWire[]): {
  unscheduled: SpiceClientRosterRowWire[];
  noTherapist: SpiceClientRosterRowWire[];
} {
  const unscheduled = clients.filter((c) => c.schedule === null || c.schedule.sessions.length === 0);
  const noTherapist = clients.filter((c) => c.therapist_roster_id === null && !unscheduled.includes(c));
  return { unscheduled, noTherapist };
}


/** What a lifted entry carries for the drop, the overlay and the announcer. */
interface EntryDragData {
  entry: SpiceWeekGridEntryWire;
  /** The row it was lifted from. */
  rosterId: string;
}

/** What a cell carries for the drop and the announcer. */
interface CellDropData extends SpiceGridCell {
  therapistName: string;
}

/** The cell under the pointer; for a keyboard step, which has no pointer,
 *  the cell the lifted entry's outline sits in. */
const cellCollision: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args);
  return underPointer.length > 0 ? underPointer : rectIntersection(args);
};

function liftedWords(active: Active): string {
  const data = active.data.current as EntryDragData | undefined;
  return data ? `${data.entry.client_display_name}'s ${weekdayLong(data.entry.weekday)} visit` : 'the visit';
}

function cellWords(over: Over): string {
  const data = over.data.current as CellDropData | undefined;
  return data ? `${data.therapistName}, ${weekdayLong(data.weekday)}` : 'a cell';
}

/** The screen reader's account of a drag, each step in the grid's words. */
const DRAG_ANNOUNCEMENTS: Announcements = {
  onDragStart: ({ active }) =>
    `Picked up ${liftedWords(active)}. Use the arrow keys to move it to another day or therapist, space to drop it, escape to put it back.`,
  onDragOver: ({ active, over }) => (over ? `${liftedWords(active)} over ${cellWords(over)}.` : `${liftedWords(active)} is over no cell.`),
  onDragEnd: ({ active, over }) => (over ? `${liftedWords(active)} dropped on ${cellWords(over)}.` : `${liftedWords(active)} put back.`),
  onDragCancel: ({ active }) => `Move cancelled. ${liftedWords(active)} stays where it was.`,
};

const DRAG_INSTRUCTIONS = {
  draggable:
    'To move this visit, press space, use the arrow keys to move it to another day or therapist, then press space again to drop it. Press escape to put it back.',
};

/** The route of a client's SPICE tab on the client record. */
function spiceTabHref(tenantSlug: string, clientId: string): string {
  return `/t/${tenantSlug}/clients/${clientId}?tab=spice`;
}

/** The editor door's accessible label, the kind named in words. */
function entryDoorLabel(entry: SpiceWeekGridEntryWire, when: string): string {
  return `Edit ${entry.client_display_name}'s ${weekdayLong(entry.weekday)} ${SPICE_KIND_LABELS[entry.kind]} session, ${when}`;
}

/** An entry's away phrase: "Covered by ... until ..." or "Paused ...". */
function entryAwayPhrase(entry: SpiceWeekGridEntryWire): string {
  return formatSpiceAway({
    state: entry.status === 'covered' ? 'covered' : 'on_hold',
    hold_reason: entry.hold_reason,
    hold_note: entry.hold_note ?? null,
    status_until: entry.status_until,
    cover_name: entry.covered_by_name,
  });
}

/** The frequency words of an entry's accessible name: ", every two weeks"
 *  or ", monthly on the second Tuesday"; nothing for weekly. */
function frequencyPart(entry: SpiceWeekGridEntryWire): string {
  if (entry.frequency === 'every_two_weeks') return ', every two weeks';
  if (entry.frequency === 'monthly' && entry.week_of_month !== null) {
    return `, monthly on the ${SPICE_WEEK_OF_MONTH_LABELS[entry.week_of_month].toLowerCase()} ${weekdayLong(entry.weekday)}`;
  }
  return '';
}

/**
 * The pill's accessible name and title, in full: the kind, the time
 * (active and covered), the client's whole name, then the frequency and
 * whose visit it is. A paused pill names its away phrase in place of the
 * time.
 *   "SPICE session, 9:00 AM, Vitus Colewhistle"
 *   "SPICE session, 10:00 AM, Dorcas Linnetfield, covering for Shanti Plumbottle"
 *   "SPICE session, 2:00 PM, Rosa Quint, covered by Cressida Mertlebottom until 16 Sep"
 *   "SPICE session, Sylvia Ruiz, Paused until 19 Sep (Vacation)"
 */
export function pillLabel(entry: SpiceWeekGridEntryWire): string {
  const kind = SPICE_KIND_LABELS[entry.kind];
  const name = entry.client_display_name;
  if (entry.status === 'on_hold') return `${kind} session, ${name}, ${entryAwayPhrase(entry)}`;
  const head = `${kind} session, ${formatSpiceTime(entry.start_time)}, ${name}${frequencyPart(entry)}`;
  if (entry.status === 'covered') {
    const until = entry.status_until ? ` until ${formatSpiceShortDate(entry.status_until)}` : '';
    return `${head}, covered by ${entry.covered_by_name ?? ''}${until}`;
  }
  if (entry.is_cover) return entry.cover_of_name ? `${head}, covering for ${entry.cover_of_name}` : head;
  return head;
}

/** The pill's look for its state: filled in the kind colour (active),
 *  outlined on paper with the kind colour's edge (covered), or greyed on
 *  sand with that edge (paused). Every look has a 1px border, so all
 *  three are one height. */
function pillLook(
  entry: SpiceWeekGridEntryWire,
  color: SpiceKindColor,
): { className: string; style: CSSProperties; background: string } {
  if (entry.status === 'covered') {
    return { className: 'bg-paper text-ink', style: { borderColor: color.fill }, background: 'var(--color-paper)' };
  }
  if (entry.status === 'on_hold') {
    return { className: 'bg-sand text-ink-soft', style: { borderColor: color.fill }, background: 'var(--color-sand)' };
  }
  return {
    className: '',
    style: { backgroundColor: color.fill, borderColor: color.fill, color: color.text },
    background: color.fill,
  };
}

const PILL_BASE =
  'relative group flex w-full min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-pill border ' +
  'px-2 py-0.5 text-xs leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-800 ' +
  'focus-visible:ring-offset-1';

/** Who a pill is drawn for: an editor (the doors and the drag), a viewer
 *  (the name link only), or the drag overlay (words only, no door). */
type PillMode = 'editor' | 'viewer' | 'overlay';

/**
 * One grid entry, drawn: a pill on one line, the time (or a paused pill's
 * away phrase) then the client's name, in the kind's colour
 * (useSpiceKindColors). The name links to the client's SPICE tab and
 * truncates with an ellipsis; the whole name stays in the pill's
 * accessible name and title (pillLabel).
 *
 * For an editor:
 *   - the time, or a paused pill's phrase, opens the schedule editor;
 *   - the pill that is the client's own (not a cover's) is the drag
 *     handle (DraggablePill), lifted by pointer from anywhere on it or
 *     by keyboard with space on the pill itself; space
 *     on the time, the name or the × never lifts, and the browser never
 *     drags the name (draggable false);
 *   - its × removes that session. It is overlaid on the pill's right end,
 *     so it costs no width at rest, and shown by opacity only: on hover,
 *     on focus inside the pill, and always where the device has no hover.
 *     A pointer press on it never starts a drag.
 * A covered pill (the regular therapist's row while another covers) reads
 * "Covering: {name}" in one quiet line under it.
 */
function PillView({
  entry,
  tenantSlug,
  mode,
  busy,
  draggable,
  isDragging,
  nodeRef,
  dragProps,
  onEdit,
  onRemove,
}: {
  entry: SpiceWeekGridEntryWire;
  tenantSlug: string;
  mode: PillMode;
  busy: boolean;
  draggable: boolean;
  isDragging: boolean;
  nodeRef?: (el: HTMLDivElement | null) => void;
  dragProps?: Record<string, unknown>;
  onEdit?: (entry: SpiceWeekGridEntryWire) => void;
  onRemove?: (entry: SpiceWeekGridEntryWire) => void;
}): ReactNode {
  const colors = useSpiceKindColors();
  const own = !entry.is_cover;
  const editor = mode === 'editor';
  const look = pillLook(entry, colors[entry.kind]);
  const label = pillLabel(entry);
  const paused = entry.status === 'on_hold';
  const time = formatSpiceTime(entry.start_time);

  let door: ReactNode;
  if (paused) {
    door = editor ? (
      <Button
        variant="ghost"
        size="sm"
        data-testid="spice-week-entry-hold"
        aria-label={entryDoorLabel(entry, 'paused')}
        className="!block !min-h-0 min-w-0 flex-1 truncate !p-0 text-left !text-xs !font-normal !text-inherit hover:underline focus-visible:!ring-current"
        onClick={() => onEdit?.(entry)}
      >
        {entryAwayPhrase(entry)}
      </Button>
    ) : (
      <span className="min-w-0 flex-1 truncate">{entryAwayPhrase(entry)}</span>
    );
  } else {
    door = editor ? (
      <Button
        variant="ghost"
        size="sm"
        data-testid="spice-week-entry-time"
        aria-label={entryDoorLabel(entry, time)}
        className="!inline !min-h-0 shrink-0 !p-0 !text-xs !font-semibold !text-inherit hover:underline focus-visible:!ring-current"
        onClick={() => onEdit?.(entry)}
      >
        {time}
      </Button>
    ) : (
      <span className="shrink-0 font-semibold">{time}</span>
    );
  }

  const nameClass = paused ? 'shrink-0 max-w-[70%] truncate' : 'min-w-0 flex-1 truncate';
  const name =
    mode === 'overlay' ? (
      <span className={nameClass}>{entry.client_display_name}</span>
    ) : (
      <Link
        data-testid="spice-week-entry-name"
        to={spiceTabHref(tenantSlug, entry.client_id)}
        title={entry.client_display_name}
        draggable={false}
        className={`${nameClass} hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current rounded-tight`}
      >
        {entry.client_display_name}
      </Link>
    );

  return (
    <>
      <div
        ref={nodeRef}
        {...dragProps}
        role="group"
        aria-label={label}
        title={label}
        data-testid="spice-week-entry"
        data-status={entry.status}
        data-kind={entry.kind}
        className={[
          PILL_BASE,
          look.className,
          draggable ? 'cursor-grab touch-none active:cursor-grabbing' : '',
          isDragging ? 'opacity-40' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={look.style}
      >
        {door}
        {name}
        {editor && own && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="spice-week-entry-remove"
            aria-label={`Remove ${entry.client_display_name}'s ${weekdayLong(entry.weekday)} visit`}
            title="Remove this visit"
            disabled={busy}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onRemove?.(entry)}
            // The shared Button is position: relative; the × sits over the
            // pill's right end, so its own position wins here.
            style={{ backgroundColor: look.background, position: 'absolute' }}
            className="absolute right-1 top-1/2 -translate-y-1/2 !min-h-0 !rounded-pill !px-1 !py-0 !text-xs !text-inherit opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 focus-visible:!ring-current"
          >
            ×
          </Button>
        )}
      </div>
      {entry.status === 'covered' && mode !== 'overlay' && (
        <p data-testid="spice-week-entry-covering" className="truncate px-2 text-xs text-ink-soft">
          Covering: {entry.covered_by_name}
        </p>
      )}
    </>
  );
}

/** An editor's pill that is the client's own, registered as a draggable:
 *  it lifts unless another move or removal is in flight. Only these pills
 *  register, so a cover's or a shared therapist's pill for the same visit
 *  never takes the drag's id. */
function DraggablePill({
  entry,
  rosterId,
  tenantSlug,
  busy,
  onEdit,
  onRemove,
}: {
  entry: SpiceWeekGridEntryWire;
  rosterId: string;
  tenantSlug: string;
  busy: boolean;
  onEdit: (entry: SpiceWeekGridEntryWire) => void;
  onRemove: (entry: SpiceWeekGridEntryWire) => void;
}): ReactNode {
  const draggable = !busy;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: spiceEntryDragId(entry),
    data: { entry, rosterId } satisfies EntryDragData,
    disabled: !draggable,
    attributes: { role: 'group' },
  });
  const nodeRef = (el: HTMLDivElement | null): void => {
    setNodeRef(el);
    setActivatorNodeRef(el);
  };
  return (
    <PillView
      entry={entry}
      tenantSlug={tenantSlug}
      mode="editor"
      busy={busy}
      draggable={draggable}
      isDragging={isDragging}
      nodeRef={nodeRef}
      dragProps={draggable ? { ...attributes, ...listeners } : undefined}
      onEdit={onEdit}
      onRemove={onRemove}
    />
  );
}

/** One cell: a drop target for an editor, marked while a visit is over it. */
function GridCell({
  therapist,
  weekday,
  isToday,
  canEdit,
  tenantSlug,
  busy,
  onEdit,
  onRemove,
  onAdd,
}: {
  therapist: SpiceWeekGridTherapistWire;
  weekday: number;
  isToday: boolean;
  canEdit: boolean;
  tenantSlug: string;
  busy: boolean;
  onEdit: (entry: SpiceWeekGridEntryWire) => void;
  onRemove: (entry: SpiceWeekGridEntryWire) => void;
  onAdd: () => void;
}): ReactNode {
  const { setNodeRef, isOver } = useDroppable({
    id: spiceCellDropId({ rosterId: therapist.roster_id, weekday }),
    data: { rosterId: therapist.roster_id, weekday, therapistName: therapist.full_name } satisfies CellDropData,
    disabled: !canEdit,
  });
  const entries = therapist.entries.filter((e) => e.weekday === weekday);
  return (
    <td
      ref={setNodeRef}
      className={`px-2 py-3 space-y-1 ${isOver ? 'bg-sage-100 ring-2 ring-inset ring-sage-400' : isToday ? 'bg-sage-800/[4%]' : ''}`}
      data-testid="spice-week-cell"
    >
      {entries.map((e, i) =>
        canEdit && !e.is_cover ? (
          <DraggablePill
            key={`${spiceEntryDragId(e)}-${i}`}
            entry={e}
            rosterId={therapist.roster_id}
            tenantSlug={tenantSlug}
            busy={busy}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        ) : (
          <PillView
            key={`${spiceEntryDragId(e)}-${i}`}
            entry={e}
            tenantSlug={tenantSlug}
            mode={canEdit ? 'editor' : 'viewer'}
            busy={busy}
            draggable={false}
            isDragging={false}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        ),
      )}
      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          className="!min-h-0 !px-1.5 !py-0.5 text-xs text-sage-700"
          aria-label={`Add a visit for ${therapist.full_name} on ${weekdayLong(weekday)}`}
          data-testid={`spice-week-add-${therapist.roster_id}-${weekday}`}
          onClick={onAdd}
        >
          + Add
        </Button>
      )}
    </td>
  );
}

export interface SpiceWeekTabProps {
  tenantSlug: string;
}

export function SpiceWeekTab({ tenantSlug }: SpiceWeekTabProps): ReactNode {
  const contact = useSupportContact();
  const { whoami } = useTenant();
  const canEdit = canEditClientSchedule(whoami);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<EditingTarget | null>(null);
  const [adding, setAdding] = useState<AddTarget | null>(null);
  const { push: pushUndo } = useUndo();
  const setPlan = useSetClientSpicePlan(tenantSlug);
  const deletePlan = useDeleteClientSpicePlan(tenantSlug);
  /** The entry whose move or removal is in flight; every door waits for it. */
  const [inFlight, setInFlight] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** The entry under a drag, for the overlay. */
  const [lifted, setLifted] = useState<EntryDragData | null>(null);
  /** The grid's rows in display order, read by the keyboard step. */
  const rowRosterIdsRef = useRef<string[]>([]);

  /** One arrow press while a visit is lifted by keyboard: the neighbouring
   *  cell's corner, so the lifted outline lands inside that cell. */
  const coordinateGetter = useCallback<KeyboardCoordinateGetter>((event, { context }) => {
    const data = context.active?.data.current as EntryDragData | undefined;
    if (!data) return undefined;
    const overCell = context.over?.data.current as CellDropData | undefined;
    const from: SpiceGridCell = overCell ?? { rosterId: data.rosterId, weekday: data.entry.weekday };
    const next = nextSpiceGridCell(event.code, from, rowRosterIdsRef.current);
    if (!next) return undefined;
    const rect = context.droppableRects.get(spiceCellDropId(next));
    return rect ? { x: rect.left, y: rect.top } : undefined;
  }, []);
  // A keyboard step onto a cell below the fold scrolls the page to it
  // instead of moving the outline; the scroll is instant so the cell is
  // under the outline before the next key, and a drop right after the
  // step lands on that cell, never on the one it left.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter, scrollBehavior: 'auto' }),
  );

  const weekParam = searchParams.get('week');
  const week = weekParam && ISO_DATE_RE.test(weekParam) ? isoWeekMonday(weekParam) : currentWeekMonday();
  const thisWeek = currentWeekMonday();
  const todayIso = calendarDateIn(new Date());
  const setWeek = (next: string): void => {
    const p = new URLSearchParams(searchParams);
    if (next === thisWeek) p.delete('week');
    else p.set('week', next);
    setSearchParams(p, { replace: true });
  };

  const grid = useSpiceWeek(tenantSlug, week);
  // Editors only: the all-clients SPICE view feeds the Add picker, the
  // editor's Therapist select (current therapist + engagement) and the
  // "not on the grid" list. A viewer fires no request for it.
  const spiceClients = useSpiceClientsList(tenantSlug, { enabled: canEdit });
  const clientsById = useMemo(
    () => new Map(spiceClients.clients.map((c) => [c.id, c])),
    [spiceClients.clients],
  );
  const offGrid = useMemo(() => spiceClientsOffTheGrid(spiceClients.clients), [spiceClients.clients]);

  const columns = spiceWeekColumns(grid.therapists);
  const nonEmptyTherapists = grid.therapists.filter((t) => t.entries.length > 0);
  const emptyTherapists = grid.therapists.filter((t) => t.entries.length === 0);
  // An editor needs a row (and its Add doors) for a therapist with nothing
  // this week; a viewer reads those names in one footer line instead.
  const rowTherapists: SpiceWeekGridTherapistWire[] = canEdit ? grid.therapists : nonEmptyTherapists;
  rowRosterIdsRef.current = rowTherapists.map((t) => t.roster_id);

  const therapistContextFor = (clientId: string): SpiceScheduleTherapistContext | undefined => {
    const row = clientsById.get(clientId);
    if (!row) return undefined;
    // The plan-level select opens on the client's own therapist unless the
    // door named a row for it (an Add door for a client with none yet).
    return {
      currentRosterId: row.therapist_roster_id,
      currentEngagement: row.engagement,
      initialRosterId: editing?.therapistRosterId === undefined ? row.therapist_roster_id : editing.therapistRosterId,
    };
  };

  /**
   * The per-entry remove door. A grid entry is one session on one date,
   * not the whole plan, so the client's plan is read first: it is both
   * the rule's input (one session of several, or the last) and the body
   * Undo puts back. The session is found by its weekday, time and week of
   * the month. A plan that no longer carries the entry's session (changed
   * since the grid loaded) is left alone and the grid refetched.
   */
  const removeEntry = async (entry: SpiceWeekGridEntryWire): Promise<void> => {
    const name = entry.client_display_name;
    const weekday = weekdayLong(entry.weekday);
    setInFlight(spiceEntryDragId(entry));
    setActionError(null);
    try {
      const current = await fetchClientSpicePlan(tenantSlug, entry.client_id);
      if (!current.plan) {
        grid.refetch();
        return;
      }
      const before = spicePlanToInput(current.plan);
      if (before.sessions.length > 1) {
        const index = before.sessions.findIndex(
          (s) =>
            s.weekday === entry.weekday &&
            s.start_time === entry.start_time &&
            (s.week_of_month ?? null) === (entry.week_of_month ?? null),
        );
        if (index === -1) {
          setActionError(`${name}'s schedule changed since the grid loaded. It has been refreshed; try again.`);
          grid.refetch();
          return;
        }
        const bodies = withSessionTherapistKeys(spicePlanWithoutSession(before, index), before);
        await setPlan.mutateAsync({ clientId: entry.client_id, input: bodies.forward });
        pushUndo({
          label: `${name}'s ${weekday} session removed`,
          undo: async () => {
            await setPlan.mutateAsync({ clientId: entry.client_id, input: bodies.before });
          },
        });
      } else {
        await deletePlan.mutateAsync({ clientId: entry.client_id });
        pushUndo({
          label: `${name}'s SPICE schedule removed`,
          undo: async () => {
            await setPlan.mutateAsync({ clientId: entry.client_id, input: before });
          },
        });
      }
    } catch {
      setActionError(
        contactSentence(`${name}'s visit wasn't removed. Try again, and if it happens again, tell {contact_system}.`, contact),
      );
    } finally {
      setInFlight(null);
    }
  };

  /**
   * A dropped entry. A drop back on its own cell is nothing, before any
   * read. Otherwise the client's plan is read first (a grid entry is one
   * session on one date, not the whole plan): it is both what
   * planSpiceMove decides on, with the week in view and the client's own
   * therapist, and the body Undo puts back. Either decision is ONE plan
   * PUT that changes only the dragged session: its day on its own row,
   * its therapist and day on another therapist's row. No drag writes the
   * engagement. The forward body and the Undo body name every session's
   * therapist whenever either names one (withSessionTherapistKeys). A
   * drop the server refuses says why on the grid: a row's therapist who
   * cannot take SPICE visits (422), a page that must reload (409), or
   * the house line for anything else.
   */
  const moveEntry = async (from: EntryDragData, to: CellDropData): Promise<void> => {
    const { entry } = from;
    if (to.rosterId === from.rosterId && to.weekday === entry.weekday) return;
    const name = entry.client_display_name;
    const fromDay = weekdayLong(entry.weekday);
    const toDay = weekdayLong(to.weekday);
    setInFlight(spiceEntryDragId(entry));
    setActionError(null);
    try {
      const current = await fetchClientSpicePlan(tenantSlug, entry.client_id);
      if (!current.plan) {
        grid.refetch();
        return;
      }
      const before = spicePlanToInput(current.plan);
      const decision = planSpiceMove({
        entry,
        fromRosterId: from.rosterId,
        toRosterId: to.rosterId,
        toWeekday: to.weekday,
        plan: before,
        therapistRosterIds: new Set(grid.therapists.map((t) => t.roster_id)),
        weekStart: week,
        leadRosterId: current.therapist_roster_id,
      });
      if (decision.kind === 'same-cell') return;
      // A monthly session dropped on another day names its new week.
      const movedIndex = before.sessions.findIndex(
        (s) =>
          s.weekday === entry.weekday &&
          s.start_time === entry.start_time &&
          (s.week_of_month ?? null) === (entry.week_of_month ?? null),
      );
      const movedSession = decision.kind === 'refused' ? undefined : decision.input.sessions[movedIndex];
      const monthlyWords =
        entry.frequency === 'monthly' && to.weekday !== entry.weekday && movedSession?.week_of_month
          ? `, now monthly on the ${SPICE_WEEK_OF_MONTH_LABELS[movedSession.week_of_month].toLowerCase()} ${toDay}`
          : '';
      if (decision.kind === 'refused') {
        setActionError(spiceMoveRefusalLine(decision.reason, name));
        if (decision.reason === 'slot-not-on-plan') grid.refetch();
        return;
      }
      const bodies = withSessionTherapistKeys(decision.input, before);
      try {
        await setPlan.mutateAsync({ clientId: entry.client_id, input: bodies.forward });
      } catch (err) {
        setActionError(moveRefusedLine(err, name, to.therapistName, contact));
        return;
      }
      pushUndo({
        label:
          decision.kind === 'weekday-only'
            ? `${name}'s ${fromDay} visit moved to ${toDay}${monthlyWords}`
            : `${name}'s ${fromDay} visit moved to ${to.therapistName}${to.weekday === entry.weekday ? '' : `, ${toDay}`}${monthlyWords}`,
        undo: async () => {
          await setPlan.mutateAsync({ clientId: entry.client_id, input: bodies.before });
        },
      });
    } catch {
      setActionError(moveFailedLine(name, contact));
    } finally {
      setInFlight(null);
    }
  };

  const onDragStart = (e: DragStartEvent): void => {
    setLifted((e.active.data.current as EntryDragData | undefined) ?? null);
  };
  const onDragCancel = (): void => setLifted(null);
  const onDragEnd = (e: DragEndEvent): void => {
    setLifted(null);
    const from = e.active.data.current as EntryDragData | undefined;
    const to = e.over?.data.current as CellDropData | undefined;
    if (!from || !to) return;
    void moveEntry(from, to);
  };

  const openEntry = (entry: SpiceWeekGridEntryWire): void => {
    // A pill opens the window on the client's own therapist, whichever
    // row it sits on: the session's own therapist is on its card, and a
    // cover entry sits on the cover's row, not the client's therapist's.
    setEditing({
      clientId: entry.client_id,
      clientName: entry.client_display_name,
      date: addDays(week, entry.weekday - 1),
    });
  };

  return (
    <div data-testid="spice-week-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="spice-week-picker">
        <Button variant="secondary" size="sm" onClick={() => setWeek(addDays(week, -7))} data-testid="spice-week-prev">
          Previous week
        </Button>
        <span className="text-sm font-semibold text-sage-800" data-testid="spice-week-label">
          {weekLabel(week)}
          {week === thisWeek ? ' (this week)' : ''}
        </span>
        <Button variant="secondary" size="sm" onClick={() => setWeek(addDays(week, 7))} data-testid="spice-week-next">
          Next week
        </Button>
        {week !== thisWeek && (
          <Button variant="ghost" size="sm" onClick={() => setWeek(thisWeek)} data-testid="spice-week-today">
            This week
          </Button>
        )}
        <Link
          to={`/t/${tenantSlug}/clients/all?view=spice`}
          className="ml-auto text-xs text-sage-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
          data-testid="all-spice-clients-link"
        >
          All SPICE clients →
        </Link>
      </div>

      {canEdit && (
        <p className="mb-3 text-xs text-ink-soft" data-testid="spice-week-edit-help">
          Click a visit's time to change its sessions or therapist; its name opens the client's SPICE tab; the × inside
          it removes that session. Drag a visit to another day or onto another therapist's row to move it there (or
          focus the visit, press space and use the arrow keys). Use Add in any cell to schedule a client with that
          therapist on that day. Changes save to the client's record; the grid is the same schedule, not a copy.
        </p>
      )}
      {actionError !== null && (
        <p role="alert" className="mb-3 text-xs text-pepper" data-testid="spice-week-action-error">
          {actionError}
        </p>
      )}

      {grid.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}
      {grid.isError && <ErrorBanner error={grid.error} onRetry={grid.refetch} what="the SPICE week" />}

      {!grid.isLoading && !grid.isError && grid.therapists.length === 0 && (
        <p className="py-6 text-sm text-ink-soft" data-testid="spice-week-empty">
          Mark who does SPICE on the People page and you'll see them here.
        </p>
      )}

      {!grid.isLoading && !grid.isError && grid.therapists.length > 0 && rowTherapists.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={cellCollision}
          accessibility={{ announcements: DRAG_ANNOUNCEMENTS, screenReaderInstructions: DRAG_INSTRUCTIONS }}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <div className="mb-2">
            <SpiceKindLegend testId="spice-week-legend" />
          </div>
          <div className="overflow-x-auto rounded-tight border border-sage-800/[8%]" data-testid="spice-week-grid-scroll">
            {/* Fixed layout: the therapist column takes a set width and the
                days share the rest equally, each day at least 7rem, so a
                long name never widens its own day while the others squeeze.
                Below that floor the grid scrolls inside this wrapper; the
                page itself never scrolls sideways. */}
            <table
              className="w-full table-fixed border-collapse"
              style={{ minWidth: `${9 + columns.length * 7}rem` }}
              data-testid="spice-week-grid"
            >
              <colgroup>
                <col className="w-36" />
                {columns.map((d) => (
                  <col key={d.weekday} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-sage-800/[8%] bg-sand/40">
                  <th scope="col" className="px-2 py-2 text-left text-label tracking-label font-semibold uppercase text-sage-400">
                    Therapist
                  </th>
                  {columns.map((d) => {
                    const isToday = addDays(week, d.weekday - 1) === todayIso;
                    return (
                      <th
                        key={d.weekday}
                        scope="col"
                        data-testid={isToday ? 'spice-week-today-column' : undefined}
                        className={`px-2 py-2 text-left text-label tracking-label font-semibold uppercase text-sage-400 ${
                          isToday ? 'bg-sage-800/[4%]' : ''
                        }`}
                      >
                        {d.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rowTherapists.map((t) => (
                  <tr
                    key={t.roster_id}
                    className="border-b border-sage-800/[6%] align-top"
                    data-testid="spice-week-row"
                    data-roster-id={t.roster_id}
                  >
                    <td className="px-2 py-3 text-sm font-medium text-sage-800">{t.full_name}</td>
                    {columns.map((d) => (
                      <GridCell
                        key={d.weekday}
                        therapist={t}
                        weekday={d.weekday}
                        isToday={addDays(week, d.weekday - 1) === todayIso}
                        canEdit={canEdit}
                        tenantSlug={tenantSlug}
                        busy={inFlight !== null}
                        onEdit={(entry) => openEntry(entry)}
                        onRemove={(entry) => void removeEntry(entry)}
                        onAdd={() =>
                          setAdding({
                            therapistRosterId: t.roster_id,
                            therapistName: t.full_name,
                            weekday: d.weekday,
                            date: addDays(week, d.weekday - 1),
                          })
                        }
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The lifted pill follows the pointer, words only; the pill itself
              stays in its cell, dimmed, until the save moves it. */}
          <DragOverlay>
            {lifted && (
              <div className="rounded-pill shadow-sh2" data-testid="spice-week-drag-overlay">
                <PillView entry={lifted.entry} tenantSlug={tenantSlug} mode="overlay" busy={false} draggable={false} isDragging={false} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {!grid.isLoading && !grid.isError && !canEdit && grid.therapists.length > 0 && emptyTherapists.length > 0 && (
        <p className="mt-3 text-sm text-ink-soft" data-testid="spice-week-no-visits">
          No visits this week: {emptyTherapists.map((t) => t.full_name).join(', ')}
        </p>
      )}

      {canEdit && !spiceClients.isLoading && (offGrid.unscheduled.length > 0 || offGrid.noTherapist.length > 0) && (
        <div className="mt-4 flex flex-col gap-2 text-sm" data-testid="spice-week-off-grid">
          {offGrid.unscheduled.length > 0 && (
            <div>
              <p className="font-semibold text-sage-800">Not scheduled yet</p>
              <p className="text-xs text-ink-soft">Click a client to set their schedule.</p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {offGrid.unscheduled.map((c) => (
                  <li key={c.id}>
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid={`spice-week-unscheduled-${c.id}`}
                      onClick={() => setEditing({ clientId: c.id, clientName: c.display_name })}
                    >
                      {c.display_name}
                      {c.therapist_name ? '' : ' (no therapist yet)'}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {offGrid.noTherapist.length > 0 && (
            <div>
              <p className="font-semibold text-sage-800">Scheduled, but no therapist yet</p>
              <p className="text-xs text-ink-soft">Click a client to choose their therapist.</p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {offGrid.noTherapist.map((c) => (
                  <li key={c.id}>
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid={`spice-week-no-therapist-${c.id}`}
                      onClick={() => setEditing({ clientId: c.id, clientName: c.display_name })}
                    >
                      {c.display_name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {adding && (
        <AddVisitPicker
          target={adding}
          clients={spiceClients.clients}
          isLoading={spiceClients.isLoading}
          isError={spiceClients.isError}
          onClose={() => setAdding(null)}
          onPick={(c) => {
            // A client with a therapist and a schedule keeps her therapist:
            // the session added takes this row's. A client with no
            // therapist yet, or no schedule yet, gets this row's therapist
            // as her own.
            const keepsTherapist =
              c.therapist_roster_id !== null && c.schedule !== null && c.therapist_roster_id !== adding.therapistRosterId;
            setEditing({
              clientId: c.id,
              clientName: c.display_name,
              therapistRosterId: keepsTherapist ? undefined : adding.therapistRosterId,
              sessionTherapistRosterId: keepsTherapist ? adding.therapistRosterId : undefined,
              weekday: adding.weekday,
              date: adding.date,
            });
            setAdding(null);
          }}
        />
      )}

      {editing && (
        <GridScheduleEditDialog
          tenantSlug={tenantSlug}
          target={editing}
          therapistContext={therapistContextFor(editing.clientId)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
